import { z } from 'zod';

// Risk analysis parameters stored on a batch (customs trade only).
export const BatchRiskAnalysisParametersSchema = z.object({
  evaluationMethods: z.record(z.any()).optional(),
  vatValidationEnabled: z.boolean().optional(),
  eoriValidationEnabled: z.boolean().optional(),
  agentPrompt: z.string().optional(),
});

export type BatchRiskAnalysisParametersInput = z.infer<typeof BatchRiskAnalysisParametersSchema>;

// Multipart bodies (batch upload) carry the parameters as a JSON string; decode it before validating.
const parseJsonFormField = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  if (!value.trim()) return undefined;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export const BatchRiskAnalysisParametersFormFieldSchema = z.preprocess(parseJsonFormField, BatchRiskAnalysisParametersSchema.optional());

const EvaluationMethodsSchema = z
  .object({
    evalMethodCalculator: z
      .object({
        activateMethod: z.boolean().optional().nullable(),
        marketplaceFees: z.number().optional().nullable(),
        fullfillmentCost: z.number().optional().nullable(),
        storageCost: z.number().optional().nullable(),
        otherCost: z.number().optional().nullable(),
        priceDeviation: z.number().optional().nullable(),
      })
      .optional(),
    evalMethodWebValue: z
      .object({
        activateMethod: z.boolean().optional().nullable(),
        minimumRate: z.number().optional().nullable(),
      })
      .optional(),
    evalMethodIMADBStatisticalValue: z
      .object({
        activateMethod: z.boolean().optional().nullable(),
        minimumRate: z.number().optional().nullable(),
      })
      .optional(),
    evalMethodInspection: z
      .object({
        periodOfInspection: z.number().optional().nullable(),
        inspectionRateOfVAT: z.number().optional().nullable(),
        inspectionRateOfEORI: z.number().optional().nullable(),
        activateMethod: z.boolean().optional().nullable(),
      })
      .optional(),
    evalMethodAIHSCode: z
      .object({
        range: z
          .object({
            from: z.number().min(0, 'From value should be greater then 0').max(1, 'From value should be less then 1'),
            to: z.number().min(0, 'To value should be greater then 0').max(1, 'To value should be less then 1'),
          })
          .superRefine(({ from, to }, ctx) => {
            if (from > to) {
              ctx.addIssue({ path: ['from'], message: 'From value must be less than to value', code: 'custom' });
              ctx.addIssue({ path: ['to'], message: 'To value must be greater than from value', code: 'custom' });
            }
          }),
        activateMethod: z.boolean().optional().nullable(),
      })
      .optional(),
  })
  .superRefine(({ evalMethodCalculator, evalMethodWebValue, evalMethodIMADBStatisticalValue }, ctx) => {
    if (evalMethodCalculator?.activateMethod && evalMethodWebValue?.activateMethod) {
      ctx.addIssue({
        path: ['evalMethodCalculator', 'activateMethod'],
        message: 'You cannot select both `EvalMethod Calculator` and `EvalMethod Web Value`',
        code: 'custom',
      });
    }

    if (Number(evalMethodWebValue?.minimumRate) > 1) {
      ctx.addIssue({
        path: ['evalMethodWebValue', 'minimumRate'],
        message: 'Evaluation method web value minimum rate should be less then 100%',
        code: 'custom',
      });
    }

    if (Number(evalMethodIMADBStatisticalValue?.minimumRate) > 1) {
      ctx.addIssue({
        path: ['evalMethodIMADBStatisticalValue', 'minimumRate'],
        message: 'Evaluation method IMADB statistical value minimum rate should be less then 100%',
        code: 'custom',
      });
    }
  });

export const ExternalBatchRiskAnalysisParametersSchema = z.object({
  evaluationMethods: EvaluationMethodsSchema.optional(),
  vatValidationEnabled: z.boolean().optional().nullable(),
  eoriValidationEnabled: z.boolean().optional().nullable(),
  agentPrompt: z.string().optional().nullable(),
});

export const ExternalBatchRiskAnalysisParametersFormFieldSchema = z.preprocess(parseJsonFormField, ExternalBatchRiskAnalysisParametersSchema.optional());
